import { useEffect, useState } from 'react'

import { CURRENCY_MAP, WEIGHT_UNIT_MAP } from '@lib/constants'
import { getProductBySlug } from '@lib/operations/product'
import type { CollectionProductSample, Product } from '@lib/types'

import Price from '@components/product/ProductView/Price'

type CustomizationProductDetailProps = {
	handleCloseDetails: () => void
	product: CollectionProductSample
	onSelect: () => boolean
	variant?: CollectionProductSample['sampleVariants'][0]
	updateVariantSelection: (variantId: string) => void
	errorMessage: string | null
	isSelected: boolean
	isLimitSelections: boolean
	maxSelections: number
}

export default function CustomizationProductDetails({
	handleCloseDetails,
	product,
	onSelect,
	variant,
	updateVariantSelection,
	errorMessage,
	isSelected,
	isLimitSelections,
	maxSelections
}: CustomizationProductDetailProps) {
	const [productData, setProductData] = useState<Product | null>(null)

	const selectedVariant = productData?.variants.find((pVariant) => pVariant.id === variant?.id)

	const hasStockSomeVariant = product.sampleVariants.some((v) => v.hasStock)

	const getProductData = async () => {
		if (product.handle) {
			const productInfo = await getProductBySlug(product.handle, false)
			setProductData(productInfo)
		}
	}

	const onSelectVariant = () => {
		const resp = onSelect()
		if (resp) handleCloseDetails()
	}

	useEffect(() => {
		getProductData()
	}, [])

	const {
		description = '',
		price,
		images,
		ingredients = [],
		howToUse,
		highlights = [],
		name,
		vendor,
		type
	} = productData || {}

	const [showAll, setShowAll] = useState(false)

	const toggleDetails = () => {
		setShowAll(!showAll)
	}

	const ingredientDetails = showAll ? ingredients : ingredients.slice(0, 7)
	const buttonLabel = showAll ? 'Collapse' : 'View all'
	const showButton = ingredientDetails.length > 7

	return productData ? (
		<div
			className="fixed inset-0 pt-[none] md:pt-[24px] overflow-y-auto flex items-center flex-col z-50 bg-bbx-pearl-light pb-[100px]"
			tabIndex={-1}
			role="dialog"
			aria-modal="true"
		>
			<div className="bg-white w-full max-w-[1000px] relative shadow-bbx-card">
				<button
					className="closeButton button absolute right-[-30px] top-[0px] opacity-60 hover:opacity-100"
					type="button"
					onClick={handleCloseDetails}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						height="40px"
						viewBox="0 -960 960 960"
						width="40px"
						fill="#1C1B1F"
					>
						<path d="M256-227.69 227.69-256l224-224-224-224L256-732.31l224 224 224-224L732.31-704l-224 224 224 224L704-227.69l-224-224-224 224Z" />
					</svg>
				</button>
				<div className="grid grid-cols-none gap-[60px] p-[24px] md:p-[60px] md:grid-cols-[2fr,2fr]">
					{/* TODO: handle carrousel instead of one image only */}
					<div>{images && images.length > 0 && <img src={`${images[0].url}&width=500&height=500`} alt={name} />}</div>
					<div className="text-left">
						{vendor && <span className="inline-block mb-3 text-sm font-medium">{vendor}</span>}
						<h1 className="mb-4 product title">{name}</h1>
						{/* FIXME: Waiting for spec */}
						{/* <div className="bg-bbx-glow inline-block text-white rounded-full py-[2px] px-[9px] text-sm mb-[6px]">
							{type}
						</div> */}
						<div className="mb-6 pt-[20px] mt-[20px] border-t-[1px]">
							<div className={`descriptionWrapper ${showAll ? '' : 'line-clamp-4'}`}>
								{showAll ? description : `${description.slice(0, 224)}...`}
							</div>
							{description.length > 224 && (
								<button
									className="mt-1 button pr-0 pl-0 min-w-0 underline"
									type="button"
									onClick={toggleDetails}
								>
									{showAll ? 'Show Less' : 'Read More'}
								</button>
							)}
						</div>
						{product.sampleVariants.length > 1 && (
							<div className={` ${errorMessage ? 'error' : ''} form-control mb-[18px]`}>
								<div className="w-full select cardSelect">
									<select
										value={variant?.id ?? ''}
										onChange={(event) => {
											updateVariantSelection(event.target.value)
										}}
										name="variant-selection"
										id="variant-selection"
									>
										<option value="" disabled>
											-select color-
										</option>
										{product.sampleVariants.map((sampleVariant) => (
											<option key={sampleVariant.id} value={sampleVariant.id}>
												{sampleVariant.title}
											</option>
										))}
									</select>
									{errorMessage && (
										<p className="text-bbx-red text-sm mt-[1px] absolute left-0 right-0">
											{errorMessage}
										</p>
									)}
								</div>
							</div>
						)}
						{selectedVariant && selectedVariant.weight > 0 && (
							<p>
								<span className="font-medium">Weight: </span>
								<span>
									{selectedVariant.weight}
									{selectedVariant.weightUnit && WEIGHT_UNIT_MAP[selectedVariant.weightUnit]}
								</span>
							</p>
						)}

						{/* FIXME: which price should I handle */}
						{variant && variant?.compareAtPriceV2?.amount && (
							<p className="mb-8 text-[18px] text-bbx-spark font-bold">
								{/* eslint-disable-next-line react/jsx-curly-brace-presence */}
								{`(`}
								<Price price={variant?.compareAtPriceV2} />
								{` Value)`}
							</p>
						)}
						{!isSelected && isLimitSelections ? (
							<button
								className="border-bbx-red border-2 w-full text-bbx-red pointer-events-none py-2"
								type="button"
							>
								<strong className="font-bold">
									You&#39;ve selected {maxSelections} of {maxSelections} items
								</strong>
								<br />
								Remove one to add this item
							</button>
						) : (
							<button
								className={`button button-primary whitespace-nowrap ${
									!hasStockSomeVariant || isSelected ? 'disabled' : ''
								}`}
								type="button"
								onClick={onSelectVariant}
							>
								{/* eslint-disable-next-line no-nested-ternary */}
								{!hasStockSomeVariant ? 'Out of Stock' : isSelected ? 'Selected' : 'Select'}
							</button>
						)}
					</div>
				</div>
				{((highlights && highlights?.length) ||
					howToUse ||
					(ingredientDetails && ingredientDetails.length > 0)) && (
					<div className="bg-bbx-pearl-lightest p-[24px] md:p-[60px] text-left">
						{highlights && highlights?.length < 0 && (
							<div className="pb-16 mb-16 border-b">
								<h2 className="mb-8 text-3xl title">Highlights</h2>
								<ul className="flex flex-wrap items-center gap-x-16 gap-y-8">
									<li className="flex items-center">
										<div className="flex items-center justify-center mr-3 text-2xl rounded-full w-14 h-14 bg-bbx-pearl text-bbx-spark">
											<i className="fa-regular fa-sheep" aria-hidden="true" />
										</div>
										<span className="font-medium">Not Tested On Animals</span>
									</li>
									{highlights.map((highlight) => (
										<li key={`${highlight.name}-${highlight.icon}`} className="flex items-center">
											{/* Icon */}
											<div className="flex items-center justify-center mr-3 text-2xl rounded-full w-14 h-14 bg-bbx-pearl text-bbx-spark">
												<i className={`fa-regular fa-${highlight.icon}`} />
											</div>
											<span className="font-medium">{highlight.name}</span>
										</li>
									))}
								</ul>
							</div>
						)}

						{(howToUse || (ingredientDetails && ingredientDetails.length > 0)) && (
							<div className="grid grid-cols-1 gap-16 md:grid-cols-2">
								{howToUse && (
									<div>
										<h2 className="mb-4 text-3xl title">How to use it</h2>
										<div className="max-w-[480px]">
											<p>{howToUse}</p>
										</div>
									</div>
								)}

								{ingredientDetails && ingredientDetails.length > 0 && (
									<div className="p-8 bg-white">
										<h2 className="mb-4 text-xl font-medium">Ingredients</h2>
										<div>
											<ul className="break-all">
												{ingredientDetails.map((item, index) => (
													// eslint-disable-next-line react/no-array-index-key
													<li key={index}>{item}</li>
												))}
											</ul>
											{showButton && (
												<button
													className="mt-1 button button-link"
													type="button"
													onClick={toggleDetails}
												>
													{buttonLabel}
												</button>
											)}
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	) : null
}
